import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/styles';
import { Snackbar, IconButton, Slide, Button, Tooltip } from '@material-ui/core';
import { Pause, Close } from '@material-ui/icons';

import { select, actions, thunks } from 'store/toolkit';
import useReturnFocus from 'hooks/useReturnFocus';
import { useFocusTo } from 'utils/FocusRefContext';

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: theme.shadows[4],
    borderRadius: theme.shape.borderRadius * 3,
  },
  icon: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  action: {
    '& svg': { color: theme.palette.grey[700] }, // accessible color for all severities
  },
}));

function TransitionDown(props) {
  const dispatch = useDispatch();

  const handleExit = useCallback(() => {
    dispatch(actions.notifications.clear());
  }, [dispatch]);

  return <Slide {...props} onExited={handleExit} direction="down" />;
}

function Notification() {
  const classes = useStyles();
  const store = useStore();
  // redux state and dispatch
  const message = useSelector(select.notifications.message);
  const isOpen = useSelector(select.notifications.isOpen);
  const severity = useSelector(select.notifications.severity);
  const duration = useSelector(select.notifications.duration);
  const returnFocusKey = useSelector(select.notifications.returnFocusKey);
  const actionButton = useSelector(select.notifications.actionButton);
  const dispatch = useDispatch();
  // global focus anchors
  const focusTo = useFocusTo();
  // refs and focus control
  const { ref: pauseRef, returnFocus: focusPause } = useReturnFocus();
  const { ref: closeRef, returnFocus: focusClose } = useReturnFocus();

  useEffect(() => {
    // when opened, focus the correct button
    if (isOpen) {
      if (duration) focusPause();
      else focusClose();
    }
  }, [isOpen, duration, focusClose, focusPause]);

  const handlePause = useCallback(() => {
    dispatch(actions.notifications.pause());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(actions.notifications.hide());

    if (returnFocusKey) {
      /* If a returnFocusKey was passed when notification was created,
       * focus should be returned to that global focus anchor when notification
       * is closed.
       */
      focusTo.key(returnFocusKey);
    }
  }, [dispatch, focusTo, returnFocusKey]);

  const handleActionButtonClick = useCallback(async () => {
    if (!actionButton) return;

    const { actionType, actionData } = actionButton;

    switch (actionType) {
      // Built to extend functionality to other action types in the future
      case 'useIncomingNetwork': {
        const { networkSlug } = actionData || {};
        await dispatch(thunks.app.userLoggedIn({ networkSlug }));
        if (select.search.isReadyForSearch(store.getState())) {
          dispatch(thunks.results.executeSearch());
        }
        break;
      }
      default:
        break;
    }

    dispatch(actions.notifications.hide());
  }, [actionButton, dispatch, store]);

  if (!isOpen) return null;

  return (
    <Snackbar
      open
      autoHideDuration={duration}
      TransitionComponent={TransitionDown}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      onClose={handleClose}
    >
      <Alert
        severity={severity}
        classes={classes}
        action={
          <>
            {actionButton && (
              <Tooltip title={actionButton.description || ''} arrow placement="top">
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={handleActionButtonClick}
                  style={{ marginRight: 8, textTransform: 'none' }}
                >
                  {actionButton.label}
                </Button>
              </Tooltip>
            )}
            {
              // if there is a duration, then autoClose is running and we show pause button
              duration && (
                <IconButton aria-label="Pause notification" ref={pauseRef} onClick={handlePause}>
                  <Pause />
                </IconButton>
              )
            }
            <IconButton aria-label="Dismiss notification" ref={closeRef} onClick={handleClose}>
              <Close />
            </IconButton>
          </>
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
}

export default React.memo(Notification);
